import { fetcher } from "common/api/Fetcher";
import { IOrgData, IOrgPostData, OrgRecord } from "common/records/Org";
import { toJSON as addressToJSON } from "common/records/Address";
import { toJSON as preferencesToJSON } from "common/records/Preference";
import { toJSON as settingsToJSON } from "common/records/Settings";
import { IOrgOverviewData } from "common/records/OrgOverview";
import { baseCleanProps, orgIncludes, orgOverviewIncludes } from "common/api/Api";
import { ITokenData, TokenKind } from "common/records/Token";
import { ITaskData } from "common/records/Task";
import { OrgParamsRecord } from "common/records/index/OrgParams";
import { toParams } from "common/records/index/QueryParamsBase";

export const OrgStatuses = <const>[
  "created",
  "paid",
  "waiting_on_materials",
  "build_in_progress",
  "build_completed",
  "ongoing_support",
  "driving_utilization",
  "training_scheduled",
  "suspended",
  "awol",
  "canceled",
  "retention",
  "held_to_contract",
  "collections",
];
export type OrgStatus = (typeof OrgStatuses)[number];

export interface IOrgResponse {
  org: IOrgData;
}

export interface IOrgOverviewResponse {
  orgs: IOrgOverviewData[];
  meta: {
    pagination: {
      current_page: number;
      total_count: number;
      total_pages: number;
    };
  };
}

export interface ITestParams {
  kind: TokenKind;
  token: Omit<ITokenData, "images">;
}

export interface ISyncParams {
  integration: TokenKind;
  process: string;
}

const url = "/api/v1/orgs";

export const list = (queryParams: OrgParamsRecord): Promise<IOrgOverviewResponse> => {
  const parameters = toParams(queryParams);
  parameters["include[]"] = orgOverviewIncludes;

  return fetcher.get(url, parameters);
};

export const load = (id: number): Promise<IOrgResponse> => {
  let actionUrl = fetcher.joinUrls(url, id.toString());

  actionUrl = fetcher.urlOptions(actionUrl, {
    "include[]": orgIncludes,
  });
  return fetcher.get(actionUrl);
};

export const update = async (org: OrgRecord): Promise<IOrgResponse> => {
  const actionUrl = fetcher.joinUrls(url, org.id.toString());

  const orgPostData = cleanProps(org);

  return await fetcher.patch<IOrgResponse>(actionUrl, {
    org: orgPostData,
    include: orgIncludes,
  });
};

export const duplicate = (
  targetId: number,
  source_org_id: number,
  data_types: string[],
): Promise<{ task: ITaskData }> => {
  const actionUrl = fetcher.joinUrls(url, targetId.toString(), "duplicate");
  return fetcher.patch(actionUrl, { source_org_id, data_types });
};

export const test = (orgId: number, params: ITestParams): Promise<{ task: ITaskData }> => {
  const actionUrl = fetcher.joinUrls(url, orgId.toString(), "test", params.kind);
  return fetcher.post(actionUrl, params);
};

export const sync = (orgId: number, params: ISyncParams) => {
  let integration = params.integration as string;
  if (integration === "marketsharp") {
    integration = "market_sharp";
  }
  const actionUrl = fetcher.joinUrls(url, orgId.toString(), "sync", integration);
  return fetcher.post(actionUrl, params);
};

export const loadCustomerOrg = (uuid: string): Promise<IOrgResponse> => {
  const actionUrl = fetcher.joinUrls(url, "uuid", uuid);

  return fetch(actionUrl)
    .then(fetcher.standardStatus)
    .then(fetcher.json<IOrgResponse>);
};

export const cleanProps = (org: OrgRecord): IOrgPostData => {
  const json: IOrgPostData = baseCleanProps(org.toJSON() as any as IOrgPostData);

  json.address_attributes = addressToJSON(org.address);
  //@ts-ignore
  delete json.address;

  json.preferences_attributes = preferencesToJSON(org.preferences);
  //@ts-ignore
  delete json.preferences;

  json.settings_attributes = settingsToJSON(org.settings);
  //@ts-ignore
  delete json.settings;

  if (org.contract_languages.size > 0) {
    json.contract_languages_attributes = org.contract_languages
      .map((cl) => {
        const contractLanguageJS = cl.toJS();
        //@ts-ignore
        if (!contractLanguageJS.id) delete contractLanguageJS.id;
        return contractLanguageJS;
      })
      .toArray();
  }

  //@ts-ignore
  delete json.contract_languages;

  //@ts-ignore
  delete json.images;

  //@ts-ignore
  delete json.image_ids;

  //@ts-ignore
  delete json.activated_price_list;
  //@ts-ignore
  delete json.emails;

  const commission_steps = json.settings_attributes.config.estimator?.commission_calculator?.custom_config?.steps;
  if (commission_steps) {
    json.settings_attributes.config.estimator.commission_calculator.custom_config.steps = commission_steps.map(
      (step: any) => {
        const { id, ...rest } = step;
        return rest;
      },
    );
  }

  return json;
};

export const screenleapShareKinds = <const>["basic", "premium"];
export const screenleapShareOptions = <const>["BROWSER", "USER_SELECT"];
export const audioOptions = <const>["enabled", "disabled", "mute"];
export const premiumRoomTypes = <const>["peer-to-peer", "group-small", "group"];
export const screenLeapTypes = <const>["NATIVE", "IN_BROWSER"];
export const sharedNeverAlwaysValues = <const>["never", "always"];
export const optionsWithModes = <const>[...sharedNeverAlwaysValues, "customer_only", "company_only"];
export const detailedPricingOptions = <const>[
  ...optionsWithModes,
  "customer_main_only",
  "customer_option_only",
  "company_main_only",
  "company_option_only",
  "company_customer_main",
  "company_customer_option",
];
export const toolValues: string[] = <const>[
  "renoworks_masking",
  "eagleview_order",
  "google_maps",
  "bing_ortho",
  "bing_birdseye",
  "zoom_earth",
  "terra_server",
  "pictometry_online",
  "pictometry_connect",
  "map_developers_area",
  "zillow",
  "trulia",
  "greensky_link",
  "chi_overhead_door",
  "homeguard",
  "houzz_link",
  "sketchup",
  "global_transport_link",
  "service_finance_link",
  "foundation_finance_link",
  "foundation_finance_dealer_link",
  "ami_design_showcase_siding",
  "ami_design_showcase_pres_window",
  "ami_design_showcase_alside_window",
  "sighten_solar",
  "enerbank_link",
  "pella_portal",
  "eagleview_order_decra",
  "provia",
  "provia_non_portal",
  "advisar_link",
  "square_d_link",
  "lp_smartside_link",
  "york_hvac_link",
  "sigler_online_link",
  "hearth_financing_link",
  "sherwin_williams_colorsnap",
  "wellsfargo_link",
  "angies_list_link",
  "home_advisor_link",
  "gaf_quick_measure_link",
  "gaf_smart_money_link",
  "timbertech_worry_less",
  "gaf_vhr_link",
  "gaf_vent_calc_link",
  "lowes_deck_designer",
  "thermatru_design_your_door",
  "pella_lowes_product_designer",
];

export const designToolValues: string[] = <const>[
  "ca_3d_demo",
  "oc_design_eyeq",
  "gaf_vhr",
  "daltile_demo",
  "abbeycarpet_demo",
  "provia",
  "provia_non_portal",
  "plygem_visualizer",
  "renuit_cabinet_demo",
  "colorview",
  "alside_renoworks",
  "lp_smartside_visualizer",
  "sunrise_configurator",
  "royal_homeplay",
  "iko_roof_viewer",
  "square_d_ezsel",
  "paragon_visualizer",
  "timbertech_deck_designer",
  "lowes_deck_designer",
  "thermatru_design_your_door",
  "pella_lowes_product_designer",
];

export type ReportValues =
  | "eagleview_roofing"
  | "hover"
  | "quick_measure_roofing"
  | "roof_scope_roofing"
  | "plnar_interior";
export const reportValues: ReportValues[] = [
  "eagleview_roofing",
  "hover",
  "quick_measure_roofing",
  "roof_scope_roofing",
  "plnar_interior",
];
export const jobMenuValues: string[] = <const>[
  "information",
  "financing",
  "appointments",
  "tools",
  "images",
  "documents",
  "measurements",
  "inspection",
  "notes",
  "history",
  "presentations",
  "design_tools",
  "estimates",
  "proposals",
  "contracts",
  "signatures",
];
export const modulesValues: string[] = <const>["leads_module", "estimate_module"];

export const visualizationsValues: string[] = <const>["renoworks", "chameleon", "chameleon_v2", "hover"];

export const cronofyInviteValues = <const>[...sharedNeverAlwaysValues, "by_choice", "by_default"];
